import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../button';
import { PolicyMarkdown, processPolicyContent } from '../../containers/PolicyPage/policyMarkdown';
import { policyVersionLine } from '../../containers/PolicyPage/policySanitize';
import {
  ACCEPT_ADDITIONAL_DRIVER_AGREEMENT,
  POLICY_VERSION,
} from '../../services/policyService/queries';
import { graphqlErrorMessage } from '../../utils/listingHold';

const Card = styled.section`
  ${tw`mt-3 p-3 rounded-md border border-border-default bg-bg-secondary`}
`;

const Scroll = styled.div`
  ${tw`max-h-48 overflow-y-auto text-sm mb-3`}
`;

/**
 * The additional driver accepts the version that governs this trip, not the
 * current public document.
 */
export const AdditionalDriverAgreement: React.FC<{
  bookingId: string;
  versionId: string;
  onAccepted?: () => void;
}> = ({ bookingId, versionId, onAccepted }) => {
  const { data, loading } = useQuery(POLICY_VERSION, {
    variables: { id: versionId },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const version = data?.policyVersion;
  const [checked, setChecked] = useState(false);
  const [accept, acceptState] = useMutation(ACCEPT_ADDITIONAL_DRIVER_AGREEMENT);
  const line = policyVersionLine({
    effectiveDate: version?.effectiveDate,
    versionNumber: version?.versionNumber,
    approximateDates: version?.approximateDates === true,
  });

  const submit = async () => {
    try {
      await accept({
        variables: { bookingId, input: { versionIds: [versionId], platform: 'web' } },
      });
      toast.success('You accepted the rental agreement');
      onAccepted?.();
    } catch (error) {
      toast.error(graphqlErrorMessage(error, 'The agreement could not be accepted'));
    }
  };

  return (
    <Card data-testid="additional-driver-agreement">
      <p tw="font-semibold text-sm mb-2">Review and accept the rental agreement</p>
      {line && <p tw="text-xs text-text-secondary mb-2">{line}</p>}
      <Scroll>
        {loading && <p>Loading the agreement…</p>}
        {version?.contentMarkdown ? (
          <PolicyMarkdown content={processPolicyContent(version.contentMarkdown)} />
        ) : null}
      </Scroll>
      <label tw="flex items-start gap-2 text-sm mb-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={event => setChecked(event.target.checked)}
          data-testid="additional-driver-agreement-checkbox"
        />
        <span>I have read this rental agreement and I agree to it for this trip.</span>
      </label>
      <Button
        text="Accept"
        type="button"
        isDisabled={!checked || !version?.id}
        isLoading={acceptState.loading}
        onClick={submit}
        dataTestId="additional-driver-agreement-accept"
      />
    </Card>
  );
};
